import React from "react";

/**
 * @description Automatically refetches data based on a filter condition.
 * It exponentially increases the wait time between refetches until a maximum time is reached.
 *
 * @param {Object} params - Object returned from useQuery.
 * @param {Function} params.filter - If at least one item in the data matches this filter, it will trigger a refetch.
 * @param {number} [params.defaultTiming=250] - Initial wait time before refetching.
 * @param {number} [params.maxTiming=5000] - Maximum wait time before refetching.
 * @param {Object} params.data - Data to check against the filter.
 * @param {boolean} params.loading - Loading state of the query.
 * @param {Function} params.refetch - Function to call to refetch the data.
 * @returns {Object} The query data and loading state.
 */
export default function useAutoRefetchedQuery({
	filter,
	defaultTiming = 250,
	maxTiming = 5000,
	data,
	loading,
	refetch,
	...queryData
}) {
	const lastRefetchRef = React.useRef(null);
	const lastTimingRef = React.useRef(defaultTiming);

	React.useEffect(() => {
		if (loading || !data) {
			return;
		}
		let normalizedData = Array.isArray(data)
			? data
			: Object.values(data || {}).flat();

		const shouldRefetch = normalizedData.some(filter);

		if (!shouldRefetch) {
			lastTimingRef.current = defaultTiming;
			return;
		}

		if (lastRefetchRef.current) {
			return;
		}

		lastRefetchRef.current = setTimeout(() => {
			refetch().finally(() => {
				lastRefetchRef.current = null;
			});
		}, lastTimingRef.current);

		// Goes exponentially, at max we will wait maxTiming ms before refetching.
		lastTimingRef.current = Math.min(lastTimingRef.current * 2, maxTiming);
	}, [data, filter, loading, defaultTiming, maxTiming, refetch]);

	return {
		data,
		loading,
		refetch,
		...queryData
	};
}
